import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, LogOut } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FirstLoginPage — R112.
 *
 * Shown when a user's `require_password_change` flag is true — typically
 * because an admin set a temp password for them (via direct-set or bulk).
 * They can't reach any real app page until they replace it with one of
 * their own choosing.
 *
 * Uses the /auth/forgot-password → link → /auth/reset-password flow so
 * the same token machinery handles the update. We request the reset link
 * on their behalf and land them on this page with a token; if they got
 * here from a first login (no token), we call /auth/forgot-password to
 * mail them one, and offer sign-out as an escape hatch.
 */
export default function FirstLoginPage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [current, setCurrent] = useState("");
  const [loading, setLoading] = useState(false);

  const valid = pw.length >= 10 && /[a-zA-Z]/.test(pw) && /[0-9]/.test(pw) && pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      if (pw !== confirm) return toast.error("Passwords don't match");
      return toast.error("Password needs 10+ chars, one letter, one digit");
    }
    setLoading(true);
    try {
      // Step 1: request a fresh reset token for this user's email
      await axios.post(`${API}/api/auth/forgot-password`, { email: user.email },
                        { withCredentials: true });
      // Step 2: look up the freshly-issued token via a self-service endpoint —
      // simplest path is to POST directly to /auth/reset-password with the
      // password AND require the user's current temp password so we can
      // validate them without depending on the emailed link (which might
      // never arrive if email is misconfigured).
      const { data } = await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: current, new_password: pw },
        { withCredentials: true }
      );
      if (data?.ok) {
        toast.success("Password updated — welcome!");
        if (refresh) await refresh();
        const role = user?.role;
        const to = role === "admin" ? "/admin/dashboard"
                   : role === "vendor" ? "/vendor/tickets"
                   : role === "board" ? "/board/dashboard"
                   : "/resident/dashboard";
        navigate(to);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update password");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white">
        <div>Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="first-login-page">
      <Card className="w-full max-w-md bg-[#0a0a0b] border-white/10 text-white p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/prestigious-logo.png" alt="Prestigious" className="h-12 w-12" />
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">One last step</div>
            <div className="text-lg font-semibold">Welcome, {user.name}</div>
          </div>
        </div>

        <div className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200">
            Your account is using a temporary password set by an administrator.
            Please pick your own password before continuing. This screen won&apos;t
            appear again once you&apos;ve done that.
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="first-login-form">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Current temporary password</Label>
            <Input
              data-testid="first-login-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-1 bg-white/[0.04] border-white/10 text-white"
              placeholder="What the admin sent you"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">New password</Label>
            <Input
              data-testid="first-login-new"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
              required
              className="mt-1 bg-white/[0.04] border-white/10 text-white"
              placeholder="At least 10 chars with a letter + digit"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Confirm new password</Label>
            <Input
              data-testid="first-login-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className="mt-1 bg-white/[0.04] border-white/10 text-white"
            />
          </div>
          {pw && !valid && (
            <div className="text-[11px] text-rose-300">
              {pw !== confirm ? "Passwords don't match yet" : "Needs 10+ chars, at least one letter and one digit"}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="submit"
              disabled={loading || !valid || !current}
              className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full h-11"
              data-testid="first-login-submit">
              <KeyRound className="h-4 w-4 mr-1" />
              {loading ? "Updating…" : "Set my password"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-white/10 rounded-full h-11"
              onClick={() => { logout(); navigate("/login"); }}
              data-testid="first-login-signout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
